// Body-scroll guarantee.
//
// Multiple third-party plugins (Klaviyo signup popups, WooCommerce
// mini-cart drawer, occasional cookie banners) attempt to lock body
// scroll while their overlay is active. Several issues we've hit:
//
//   1. Klaviyo applies `body.klaviyo-prevent-body-scrolling` plus inline
//      `body.style.overflow = "hidden"` — and on some versions does NOT
//      cleanly remove either after the popup is dismissed, leaving the
//      page un-scrollable.
//   2. The CSS override in vendor-overrides.scss beats Klaviyo's class
//      rule, but cannot beat Klaviyo's INLINE overflow:hidden — inline
//      styles win without !important on the inline side, but we can't
//      add !important to inline styles via CSS.
//   3. WooCommerce's mini-cart drawer renders as a full-viewport fixed
//      element even when closed (transformed off-screen). On some
//      mobile browsers this confuses touch event routing and steals
//      the first touchmove from page scrolling.
//
// Strategy:
//   - On boot, force body overflow back to whatever it was without any
//     lock applied.
//   - MutationObserver watches body for attribute changes (`class` and
//     `style`). If either gains a scroll-lock signal, we immediately
//     reset it. Runs the entire session — costs nearly nothing because
//     the callback only fires on body attribute mutations (rare).
//
// Scope: site-wide. The behaviour is safe everywhere — there's no
// legitimate state where we'd want the user unable to scroll a Sentiments
// page. Modal flows that NEED scroll lock (e.g. WC's product gallery
// lightbox) use their own scroll-suppression on the inner modal content,
// not on body — so this unlock won't break them.

export function initScrollUnlock() {
	const body = document.body;
	if (!body) return;

	// Pattern: any class containing these substrings is treated as a lock
	// signal. Belt-and-braces — match whatever future plugins might add.
	const LOCK_CLASS_FRAGMENTS = [
		'klaviyo-prevent-body-scrolling',
		'no-scroll',
		'scroll-lock',
		'is-scroll-locked',
	];

	// Intentional locks that should NOT be cleared:
	//   - Our own homepage preloader (`.sentiments-preloader` is in the
	//     DOM until its curtain has finished retracting).
	const isIntentionalLock = () => {
		if (document.querySelector('.sentiments-preloader')) return true;
		return false;
	};

	const isLocked = () => {
		// Class-based lock
		for (const cls of body.classList) {
			for (const frag of LOCK_CLASS_FRAGMENTS) {
				if (cls.includes(frag)) return true;
			}
		}
		// Inline-style lock — `overflow: hidden` set directly on body
		const inlineOverflow = body.style.overflow;
		if (inlineOverflow === 'hidden') return true;
		return false;
	};

	const unlock = () => {
		// Strip lock classes
		for (const cls of [...body.classList]) {
			for (const frag of LOCK_CLASS_FRAGMENTS) {
				if (cls.includes(frag)) body.classList.remove(cls);
			}
		}
		// Strip inline overflow if set to hidden — leave anything else
		// alone so plugins that set it to e.g. `auto` (Klaviyo does)
		// aren't disturbed.
		if (body.style.overflow === 'hidden') {
			body.style.overflow = '';
		}
		// Same for html, defensively — some plugins target html instead.
		const html = document.documentElement;
		if (html.style.overflow === 'hidden') {
			html.style.overflow = '';
		}
	};

	// Immediate first pass — clear any lock left over from a previous
	// session / pre-DOMContentLoaded plugin script. Skip if our preloader
	// is intentionally locking.
	if (isLocked() && !isIntentionalLock()) unlock();

	// Observe body + html for future lock attempts and undo them on the
	// same task tick. attributes-only filter keeps the observer cheap.
	const obs = new MutationObserver(() => {
		if (isLocked() && !isIntentionalLock()) unlock();
	});
	obs.observe(body, { attributes: true, attributeFilter: ['class', 'style'] });
	obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });

	// ── Wheel scroll-rescue ────────────────────────────────────────────
	// User report: "scrollbar handle scrolls fine, but wheel/trackpad on
	// the page doesn't, AND scroll only locks once the page is fully
	// loaded." That signature is a plugin attaching wheel handlers after
	// load that either preventDefault (caught by wheel-passive-guard in
	// functions.php) OR programmatically re-set scrollY to a fixed value.
	//
	// This rescue handles the second case: after every wheel event, if
	// the window did NOT scroll AND no inner scrollable element was the
	// target, force the scroll manually. The passive guard means our
	// listener can't preventDefault; we don't want to anyway — we just
	// want to RE-DO the scroll the browser was supposed to do.
	const isInsideInnerScroller = (target) => {
		let el = target;
		while (el && el !== document.documentElement && el !== body) {
			const cs = getComputedStyle(el);
			if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') &&
			    el.scrollHeight > el.clientHeight + 5) {
				return true;
			}
			el = el.parentElement;
		}
		return false;
	};

	window.addEventListener('wheel', (e) => {
		if (!e.deltaY && !e.deltaX) return;
		if (isInsideInnerScroller(e.target)) return; // legit inner scroll

		const beforeY = window.scrollY;
		const beforeX = window.scrollX;

		requestAnimationFrame(() => {
			if (window.scrollY === beforeY && window.scrollX === beforeX) {
				// Browser didn't scroll — force it.
				window.scrollBy({
					top:  e.deltaY,
					left: e.deltaX || 0,
					behavior: 'auto',
				});
			}
		});
	}, { passive: true, capture: true });

	// ── Touch scroll-rescue (mobile) ──────────────────────────────────
	// Mobile analog of the wheel rescue above. Plugins that block touch
	// scroll typically:
	//   (a) attach `touchmove` to body/window with preventDefault — handled
	//       by the early head-script guard above (forces passive on those
	//       root-level listeners).
	//   (b) listen passively but call `window.scrollTo(0, 0)` to keep
	//       the user pinned — that's what the rescue below catches.
	//
	// We mirror native touch scroll: track touchstart Y, then on each
	// touchmove compare current Y to the start and apply the delta if the
	// browser didn't scroll on its own. Single-finger only (pinch / multi-
	// touch gestures left alone).
	let touchStartY = null;
	let touchStartScrollY = 0;
	let touchStartTarget = null;

	window.addEventListener('touchstart', (e) => {
		if (e.touches.length !== 1) {
			touchStartY = null;
			return;
		}
		touchStartY = e.touches[0].clientY;
		touchStartScrollY = window.scrollY;
		touchStartTarget = e.target;
	}, { passive: true, capture: true });

	window.addEventListener('touchmove', (e) => {
		if (touchStartY === null) return;
		if (e.touches.length !== 1) return;
		if (isInsideInnerScroller(touchStartTarget)) return;

		const currentY = e.touches[0].clientY;
		const deltaY   = touchStartY - currentY;
		if (Math.abs(deltaY) < 2) return; // ignore noise

		const expectedScrollY = touchStartScrollY + deltaY;
		const beforeY = window.scrollY;

		requestAnimationFrame(() => {
			// If the browser failed to apply ANY scroll during this gesture,
			// force it to the position the user's finger movement implies.
			if (window.scrollY === beforeY && window.scrollY !== expectedScrollY) {
				window.scrollTo({ top: expectedScrollY, behavior: 'auto' });
			}
		});
	}, { passive: true, capture: true });
}
